import Link from '@mui/material/Link';
import Typography, { TypographyProps } from '@mui/material/Typography';
import DOMPurify from 'dompurify';
import React, { useCallback, useMemo, useState } from 'react';

interface SanitizedTrimmedHtmlProps extends TypographyProps {
  html: string;
  maxLength?: number;
  hideAirshipLogo?: boolean;
  onSeeMore?: () => void;
}

const SanitizedTrimmedHtml: React.FC<SanitizedTrimmedHtmlProps> = ({
  html,
  maxLength = Infinity,
  hideAirshipLogo = false,
  onSeeMore,
  ...props
}) => {
  const [isTrimmed, setIsTrimmed] = useState(false);

  const sanitizeAirshipLogo = (html: string) => {
    if (!hideAirshipLogo) {
      return html;
    }

    const htmlElement = document.createElement('div');
    htmlElement.innerHTML = html;

    htmlElement.querySelectorAll('#airship-logo').forEach((el) => el.remove());

    return htmlElement.textContent?.trim() || '';
  };

  const sanitizedHtml = useMemo(() => {
    const sanitized = DOMPurify.sanitize(sanitizeAirshipLogo(html));

    if (maxLength === Infinity) {
      setIsTrimmed(false);
      return sanitized;
    }

    const el = document.createElement('DIV');
    el.innerHTML = sanitized;

    if (hideAirshipLogo) {
      el.querySelectorAll('#airship-logo').forEach((el) => el.remove());
    }

    const strippedText = el.textContent || el.innerText;
    const suffix = strippedText.length > maxLength ? '...' : '';

    if (strippedText.length > maxLength) {
      setIsTrimmed(true);
      return sanitized.slice(0, maxLength - 3) + suffix;
    }

    setIsTrimmed(false);
    return sanitized;
  }, [html, maxLength]);

  const onSeeMoreClicked = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onSeeMore && onSeeMore();
    },
    [onSeeMore]
  );

  return (
    <Typography component={'div'} {...props}>
      {/* Content is sanitized with DOMPurify above */}
      <span dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
      {isTrimmed && onSeeMore && (
        <>
          {' '}
          <Link
            component="button"
            onClick={onSeeMoreClicked}
            sx={{ cursor: 'pointer' }}
          >
            See more
          </Link>
        </>
      )}
    </Typography>
  );
};

export default SanitizedTrimmedHtml;
